import { InterviewRequestItem } from '../types';

export const COURSE_INTERVIEWS_CHANGED = 'idcoder:course-interviews-changed';

export function notifyCourseInterviewsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(COURSE_INTERVIEWS_CHANGED));
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function pickNumber(item: unknown, keys: string[]): number {
  const rec = asRecord(item);
  for (const key of keys) {
    const n = Number(rec[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function pickString(item: unknown, keys: string[]): string {
  const rec = asRecord(item);
  for (const key of keys) {
    const value = rec[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object') {
      const nested = asRecord(value);
      const name = nested.name ?? nested.Name;
      if (typeof name === 'string' && name.trim()) return name.trim();
    }
  }
  return '';
}

export function extractInterviewList(data: unknown): InterviewRequestItem[] {
  if (Array.isArray(data)) return data as InterviewRequestItem[];
  const rec = asRecord(data);
  const raw = rec.interviewRequests ?? rec.InterviewRequests ?? rec.items ?? rec.Items;
  if (Array.isArray(raw)) return raw as InterviewRequestItem[];
  const values = asRecord(raw).$values;
  if (Array.isArray(values)) return values as InterviewRequestItem[];
  return [];
}

export function requestStatus(item: InterviewRequestItem): string {
  return pickString(item, ['status', 'Status']);
}

export function requestSession(item: InterviewRequestItem): number {
  return pickNumber(item, ['courseInterviewSession', 'CourseInterviewSession']) || 1;
}

export function interviewBelongsToCourse(
  item: InterviewRequestItem,
  contentCourseId: number,
  courseTitle?: string,
  courseSlug?: string
): boolean {
  const courseId = Number(contentCourseId);
  const itemCourseId = pickNumber(item, ['contentCourseId', 'ContentCourseId']);
  if (courseId > 0 && itemCourseId === courseId) return true;
  const itemTitle = pickString(item, ['contentCourseTitle', 'ContentCourseTitle']).toLowerCase();
  if (courseTitle && itemTitle && itemTitle === courseTitle.trim().toLowerCase()) return true;
  if (courseSlug && itemTitle && itemTitle.includes(courseSlug.trim().toLowerCase())) return true;
  return false;
}

/** Slot is used when a non-cancelled request exists for that course session. */
export function isCourseInterviewSlotUsed(status?: string | null): boolean {
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  return normalized !== 'cancelled' && normalized !== 'inactive';
}

export function courseInterviewStatusLabel(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'active') return 'Scheduled';
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'pending') return 'Pending';
  return status;
}

/** Prefer Completed when finished; otherwise show the current status for used slots. */
export function courseInterviewSlotBadge(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'completed') return 'Completed';
  return courseInterviewStatusLabel(status);
}

export function findCourseSessionRequest(
  interviews: InterviewRequestItem[],
  contentCourseId: number,
  session: number,
  courseTitle?: string,
  courseSlug?: string
): InterviewRequestItem | undefined {
  return interviews.find((item) => {
    if (!interviewBelongsToCourse(item, contentCourseId, courseTitle, courseSlug)) return false;
    return requestSession(item) === session && isCourseInterviewSlotUsed(requestStatus(item));
  });
}

export function mapCourseSessionStatuses(
  interviews: InterviewRequestItem[],
  contentCourseId: number,
  packageCount: number,
  courseTitle?: string,
  courseSlug?: string
): Array<{ session: number; status: string | null }> {
  const courseRows = interviews.filter((item) => interviewBelongsToCourse(item, contentCourseId, courseTitle, courseSlug));
  const maxSession = courseRows.reduce((max, item) => Math.max(max, requestSession(item)), 0);
  const count = Math.max(0, packageCount, maxSession, courseRows.length);
  return Array.from({ length: count }, (_, index) => {
    const session = index + 1;
    const match = findCourseSessionRequest(interviews, contentCourseId, session, courseTitle, courseSlug);
    return { session, status: match ? requestStatus(match) : null };
  });
}
